import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import apiService from '@/services/api'
import { Group } from '@/types'

interface OrgUser {
  id: number
  name: string
  email: string
  role: string
}

interface OrgDataContextValue {
  orgUsers: OrgUser[]
  groups: Group[]
  isLoading: boolean
  refresh: () => void
}

const OrgDataContext = createContext<OrgDataContextValue>({
  orgUsers: [],
  groups: [],
  isLoading: true,
  refresh: () => {},
})

export function OrgDataProvider({ children }: { children: React.ReactNode }) {
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    setIsLoading(true)
    try {
      const [users, grps] = await Promise.all([
        apiService.getOrganizationUsers(),
        apiService.getGroups(),
      ])
      setOrgUsers(users)
      setGroups(grps)
    } catch {
      // Non-critical -- modal can still function with partial data
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return (
    <OrgDataContext.Provider value={{ orgUsers, groups, isLoading, refresh: fetchAll }}>
      {children}
    </OrgDataContext.Provider>
  )
}

export function useOrgData() {
  return useContext(OrgDataContext)
}
